import { useLangContext } from '../../providers/LangProvider/LangProviderContext';
import { IUserStatus, TUserStatuses } from './UserStatuses.t';
import {
    UserStatusPageButtonStyled,
    UserStatusPageContentStyled,
    UserStatusPageStyled,
    UserStatusPageTermsStyled,
} from './styled/UserStatusPageStyled';
import { Button, FormGroup } from 'react-bootstrap';
import React, { useCallback, useMemo, useState } from 'react';
import { USER_STATUSES_NAMES } from './userStatusesConstants';
import UserStatusesCarousel from './UserStatusesCarousel/UserStatusesCarousel';
import { useDispatch, useSelector } from 'react-redux';
import { getAppFeatures, getAppUser, getIsUserBuyerSelector } from '../../app/appSelectors';
import UserStatusRefill from './components/UserStatusRefill';
import BinIcon from '../../components/icon/Icon';
import MobileWelcomeBanner from '../../mobileAppCore/components/MobileBonusBanner/MobileWelcomeBanner';
import { hasWelcomePromocode } from '../Bonus/bonusesSelectors';
import UserStatusRespawn from './components/UserStatusRespawn';
import UserStatusPersonalManager from './components/UserStatusPersonalManager';
import UserStatusPromocodeInfo from './components/UserStatusPromocodeInfo';
import UserStatusCashback from './components/UserStatusCashback';
import amountFormat from '../../utils/amountFormat';
import { FEATURES, LINKS } from '../../app/appConstants';
import UserStatusWithdrawalLimitsToggle from './components/UserStatusWIthdrawalLimits/UserStatusWithdrawalLimitsToggle';
import { locationSlice } from '../Location/locationReducer';
import { LOCATION_NAMES } from '../Location/locationConstants';
import UserStatusStandardLotInfo from './components/UserStatusStandardLotInfo/UserStatusStandardLotInfo';
import useIsShowUserStatusWithdrawalLimits from './hooks/useIsShowUserStatusWithdrawalLimits';
import UnlimitedDemoTradesStatus from '../DemoLimits/components/UnlimitedDemoTradesStatus';
import { getIsDemoLimitsFeatureAndAbTestAvailableSelector } from '../DemoLimits/demoLimitsSelectors';

interface IUserStatusInfoProps {
    selectedStatus: TUserStatuses,
    userStatuses: IUserStatus[],
    onChangeStatus: (s: TUserStatuses) => void,
}

function UserStatusInfo(props: IUserStatusInfoProps) {
    const {
        onChangeStatus,
        selectedStatus,
        userStatuses,
    } = props;
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const user = useSelector(getAppUser);
    const isUserBuyer = useSelector(getIsUserBuyerSelector);
    const hasWelcomeBonus = useSelector(hasWelcomePromocode);
    const [showStandardLotInfo, setShowStandardLotInfo] = useState(false);
    const features = useSelector(getAppFeatures);
    const isDemoLimitsFeatureAndAbTestAvailable = useSelector(getIsDemoLimitsFeatureAndAbTestAvailableSelector);

    const userStatusParams = useMemo(() => {
        return userStatuses.find(item => item.status_name === selectedStatus);
    }, [selectedStatus, userStatuses]);

    const onClickInfoStandardLotHandler = useCallback(() => {
        setShowStandardLotInfo(true);
    }, []);

    const onCloseStandardLotInfo = useCallback(() => {
        setShowStandardLotInfo(false);
    }, []);

    const subTitle = useMemo(() => {
        if (!userStatusParams || userStatusParams.status_name === USER_STATUSES_NAMES.FREE) return null;

        let leftAmount = 0;
        switch (userStatusParams.status_state) {
            case 'progress':
                leftAmount = (userStatusParams.turnover_next - user.user_turnover) / 100000;
                break;
            case 'plan':
                leftAmount = userStatusParams.turnover / 100000;
                break;
            case 'completed':
                leftAmount = userStatusParams.turnover_next / 100000;
                break;
        }

        return <div
            className={'font-size-14 text-secondary text-center inactive d-flex justify-content-center align-items-center gap-1 cursor-pointer'}
            onClick={onClickInfoStandardLotHandler}
        >{
            !(userStatusParams.status_name === USER_STATUSES_NAMES.STANDARD && userStatusParams.status_state === 'plan') &&
            <BinIcon name={'IcInfo'}
                     size={16}/>
        } {getLangPath(`user_statuses.statuses.${selectedStatus}.subtitle_${userStatusParams?.status_state}`, {
            amount: amountFormat(leftAmount, 2),
        }, {
            emptyDefault: true,
        })}</div>;
    }, [userStatusParams, onClickInfoStandardLotHandler, getLangPath, selectedStatus, user.user_turnover]);

    const onTopUpHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({
            name: LOCATION_NAMES.DEPOSIT,
            searchParams: {
                coupon: userStatusParams?.giftPromocodeInfo?.user_promocode,
            },
        }));
    }, [dispatch, userStatusParams]);

    const onGoTradeHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.MARKET }));
    }, [dispatch]);

    const isShowWithdrawalLimits = useIsShowUserStatusWithdrawalLimits(userStatusParams);

    return <UserStatusPageStyled>
        <UserStatusStandardLotInfo onClose={onCloseStandardLotInfo} show={showStandardLotInfo}/>

        <UserStatusPageContentStyled inactive={userStatusParams?.status_state === 'completed'}>
            <h5 className={'text-center mb-0 inactive'}>{getLangPath(`user_statuses.statuses.${selectedStatus}.title`)}</h5>

            {subTitle}

            <UserStatusesCarousel
                onChangeStatus={onChangeStatus}
                selectedStatus={selectedStatus}
                userStatuses={userStatuses}
                className={'mt-3'}
            />

            {
                selectedStatus !== USER_STATUSES_NAMES.FREE && isDemoLimitsFeatureAndAbTestAvailable &&
                <div className={'mt-3'}><UnlimitedDemoTradesStatus/></div>
            }

            {
                selectedStatus === USER_STATUSES_NAMES.FREE && <FormGroup className={'mt-3'}>
                    <UserStatusRefill/>

                    {
                        !!hasWelcomeBonus && <FormGroup className={'mt-3'}>
                            <MobileWelcomeBanner short/>
                        </FormGroup>
                    }
                </FormGroup>
            }

            <div className="d-flex gap-3">
                {
                    userStatusParams?.gift_respawn && features[FEATURES.NEW_LIFE] &&
                    <FormGroup className={'mt-3 flex-1'}>
                        <UserStatusRespawn/>
                    </FormGroup>
                }

                {
                    userStatusParams?.cashback && features[FEATURES.CASHBACK] && <FormGroup className={'mt-3 flex-1'}>
                        <UserStatusCashback amount={userStatusParams.cashback}/>
                    </FormGroup>
                }
            </div>


            {
                userStatusParams?.giftPromocodeInfo && features[FEATURES.PROMOCODES] && <FormGroup className={'mt-3'}>
                    <UserStatusPromocodeInfo {...userStatusParams.giftPromocodeInfo} onTopUpHandler={onTopUpHandler}/>
                </FormGroup>
            }

            {
                isShowWithdrawalLimits && <FormGroup className={'mt-3'}>
                    <UserStatusWithdrawalLimitsToggle withdrawalLimits={userStatusParams?.withdrawalLimits}
                                                      statusState={userStatusParams?.status_state}/>
                </FormGroup>
            }

            {
                userStatusParams?.gift_manager &&
                <UserStatusPersonalManager className={'mt-3'} statusState={userStatusParams.status_state}/>
            }

            <FormGroup className={'mt-3 text-center text-secondary'}>
                <UserStatusPageTermsStyled dangerouslySetInnerHTML={{
                    __html: getLangPath('user_statuses.terms', {
                        link: LINKS.loyaltyProgramDoc,
                    }),
                }}/>
            </FormGroup>
        </UserStatusPageContentStyled>

        <UserStatusPageButtonStyled>
            {
                !isUserBuyer &&
                <Button variant={'primary'} className={'w-100'} onClick={onTopUpHandler}>
                    <span className={'me-1'}>{getLangPath('user_statuses.controls.top_up')}</span>
                    <BinIcon name={'IcPlusCircle'} size={16}/>
                </Button>
            }
            {
                isUserBuyer &&
                <Button variant={'primary'} className={'w-100'} onClick={onGoTradeHandler}>
                    <span className={'me-1'}>{getLangPath('user_statuses.controls.trade_more')}</span>
                    <BinIcon name={'IcForward'} size={16}/>
                </Button>
            }
        </UserStatusPageButtonStyled>
    </UserStatusPageStyled>;
}

export default UserStatusInfo;
